// shared-lib/cabinet-runs/families.js
//
// Cabinet PRODUCT FAMILIES (#1493, design 2026-08-27). A family belongs to
// the RUN — the unit cabinetry is bought in — never to a box; a cabinet
// resolves (family, bay type, width) → appearance. A family is a SPEC the fill
// consumes (how the run's widths are decided), not a GLB. Countertops get a
// MATERIAL, not a family.
//
// Each family is a DATA FILE in `./families/` (one JSON per family; see the
// README there). A family's `spec` says how its widths are sold — read by the
// fill engine below — and its `offering` says which door designs, paint
// colours and wood finishes it sells (`./familyOffering.js`). The three
// generic families differ in ONE axis — how a span's widths are decided — and
// that axis is `spec.fill`:
//
//   continuous  any width is buildable, so the span divides into equal boxes
//               and there is NO filler (a custom shop builds to fit).
//   grid        widths step by 3", so a span fills to the largest 3" multiple
//               and up to 3" is left as filler.
//   widths      only the listed widths are sold, so the span fills from those
//               and whatever they cannot cover is filler.
//
// `spec.widths` is what the PANEL offers as chips and snaps a typed width to;
// `familyFillOpts` is what the ENGINE solves with. They are deliberately not
// the same accessor: the grid family offers its ten chips but must reach the
// solver as the 3"-grid mode, not as an explicit width list, so that existing
// runs re-solve to exactly the boxes they already have.

const { MIN_MODULE_IN } = require('./moduleSolver');
const { unitsOf } = require('./vendorSizes');
const {
  CORNER_BLIND_SETS,
  DEFAULT_CORNER_BLIND_SET,
  pickCornerBlindIn,
  UPPER_CORNER_BLIND_SETS,
  DEFAULT_UPPER_CORNER_BLIND_SET,
  pickUpperCornerBlindIn,
  CORNER_L_SETS,
  DEFAULT_CORNER_L_SET,
} = require('../kitchen-assembly/cornerBlind');

/** The family a run with no `family` field draws from.
 *
 *  Semi-Custom, NOT Custom, and that is load-bearing: an unset family has
 *  always reached the solver as the 3"-grid decomposition, so this is the one
 *  choice under which every run persisted before families existed re-solves
 *  to the boxes it already has. Making Custom the default would silently
 *  re-cut every run in the corpus on its next re-fit. */
const DEFAULT_FAMILY_ID = 'generic-semi-custom';

// Load order is the picker's display order. Notes the JSON cannot carry:
//  - generic-custom has no width list: the panel offers the 3" chips as a
//    convenience but accepts anything, and the solver divides the span evenly.
//    Its corner unit stretches to absorb whatever the leg leaves
//    (`cornerBlindLength`) rather than standing a filler at a corner.
//  - generic-semi-custom aliases `generic`, its old name ("Generic Cabinet").
//    That blurb claimed "any width" while the engine has always run the 3"
//    grid, so the BEHAVIOUR it names is this one — the alias keeps every stored
//    run solving exactly as it did. Its widths are BASE_MODULE_WIDTHS_IN, which
//    `__tests__/familyFiles.ci.test.js` holds the file to.
const CABINET_FAMILIES = [
  require('./families/generic-custom.json'),
  require('./families/generic-semi-custom.json'),
  require('./families/generic-stock.json'),
  // The first vendor family: Tasoro's ready-to-assemble line. Its colours
  // are sold per door design (`design.finishes`), and its woods are catalog
  // textures (`unifiedTextures/tasoro-<product>`) that must exist in an
  // environment before this file ships to it — a missing one is simply not
  // offered, so the design falls back to its paint.
  require('./families/tasoro-rta.json'),
  // Venus Cabinetry: sizes from `vendors/venus` (a run builds only what that
  // catalog resolves), prices from the dated `cabinetPriceLists` edition —
  // never in the repo.
  require('./families/venus.json'),
];

/**
 * The BLIND CORNER unit's length for a leg with `freeIn` inches of clear run,
 * or 0 when the leg cannot host one. Inches in, inches out.
 *
 * Corner sizes live BESIDE `spec.widths`, never inside it: 48" is past
 * `MAX_MODULE_IN` (36"), and a corner unit is not a module — nothing may pack
 * one into a straight span. What a family contributes is only WHICH set it
 * draws from; the sets and the selection rule are
 * `kitchen-assembly/cornerBlind.js`, so the generator — which has no family —
 * reads exactly the same rule without depending on this module.
 */
function cornerBlindLength(run, freeIn) {
  return pickCornerBlindIn(cornerBlindSet(run), freeIn);
}

/**
 * The UPPER tier's blind corner wall cabinet, from the same family key: one
 * family is one way of selling widths, whichever tier asks. Inches in and out;
 * 0 when the wall cannot host one.
 */
function upperCornerBlindLength(run, freeIn) {
  return pickUpperCornerBlindIn(upperCornerBlindSet(run), freeIn);
}

/** The corner-blind size set a run's family draws from. */
function cornerBlindSet(run) {
  const key = familyOf(run)?.spec?.cornerBlind;
  return CORNER_BLIND_SETS[key] || DEFAULT_CORNER_BLIND_SET;
}

/** The same family's set for a blind corner WALL cabinet. */
function upperCornerBlindSet(run) {
  const key = familyOf(run)?.spec?.cornerBlind;
  return UPPER_CORNER_BLIND_SETS[key] || DEFAULT_UPPER_CORNER_BLIND_SET;
}

/**
 * The L corner unit sizes the same family sells (`kitchen-assembly/cornerBlind`
 * `CORNER_L_SETS`): the 36" every family builds, plus Venus's 33".
 */
function cornerLSet(run) {
  const key = familyOf(run)?.spec?.cornerBlind;
  return CORNER_L_SETS[key] || DEFAULT_CORNER_L_SET;
}

/** A family by id, resolving the ids earlier versions persisted. */
function familyById(id) {
  if (!id) return null;
  return (
    CABINET_FAMILIES.find(
      (f) => f.id === id || (f.legacyIds || []).includes(id)
    ) || null
  );
}

/**
 * The family a SCANNED run reads as when it names none: Generic Custom, which
 * builds any width — what a measured cabinet is (user ruling 2026-10-08).
 * Read as the 3"-grid Semi-Custom, a scanned cabinet showed a family its own
 * width could fall between the sizes of, and a rebuild snapped the stretches
 * between measured cabinets to that grid.
 */
const SCANNED_FAMILY_ID = 'generic-custom';

/**
 * The family a run draws from. Unset or unknown → the default: Generic Custom
 * for a scanned run, Generic Semi-Custom for any other.
 */
function familyOf(run) {
  return (
    familyById(run?.family) ||
    familyById(run?.source === 'scan' ? SCANNED_FAMILY_ID : DEFAULT_FAMILY_ID)
  );
}

/**
 * Whether a family builds cabinets to ANY depth — a custom shop, which makes a
 * 17" box for a 17" stretch. Every other line sells the standard depths only
 * (24" bases, 12" uppers; user ruling 2026-10-23), so a scanned stretch or
 * island row it cannot make is rebuilt at the standard depth or left to the
 * countertop (`standardIslandRows`). `spec.depths` in the family's file.
 */
function familyBuildsAnyDepth(family) {
  return family?.spec?.depths === 'any';
}

/**
 * Whether a run names a family THIS BUILD does not know — one added after it
 * (the dev site before Venus shipped, a tab left open across a deploy, a cloud
 * tool on older code). `familyOf` reads such a run as the default, which is
 * fine for showing it and wrong for rebuilding it: the rebuild would emit the
 * default family's cabinets under the other family's name. So a run of an
 * unknown family is never rebuilt (`materializeRun`). Unset is not unknown.
 */
function isUnknownFamily(run) {
  return Boolean(run?.family) && !familyById(run.family);
}

/**
 * The module widths (inches) a run's family OFFERS — the panel's width chips,
 * and the set a typed fixed width snaps to. Empty for a continuous family:
 * it sells every width, so there is nothing to snap to.
 */
function familyWidthsIn(run) {
  return familyOf(run)?.spec?.widths || [];
}

/**
 * The solver options a run's family imposes — what `solveModuleSequence` and
 * `solveEqualModuleSequence` are called with. One object per fill mode; the
 * grid family reaches the solver as `{}` because the 3"-grid decomposition IS
 * the solver's own default path.
 */
/**
 * The module widths a run of `family` packs from: the family's list — and,
 * for a family with a size catalog (Venus), only those widths its catalog
 * sells for this run's kind of base. Venus makes 39" and 42" kitchen bases but
 * no vanity base over 36", so one list cannot serve both profiles.
 */
function modulesSoldFor(family, run) {
  const widths = family?.spec?.widths || [];
  // required here: profiles reads families, so a top-level require would loop
  const { profileOf, VANITY_PROFILE_ID } = require('./profiles');
  // A kitchen base is any floor box with doors: Venus's 9" is its full-door
  // BF0924, not a "B09" (2026-10-09).
  const kinds =
    profileOf(run).id === VANITY_PROFILE_ID
      ? ['vanity-base']
      : ['base', 'base-full-door'];
  const sold = kinds.flatMap((kind) => unitsOf(family, kind));
  if (!sold.length) return widths;
  const made = new Set(sold.map((u) => u.w));
  return widths.filter((w) => made.has(w));
}

function familyFillOpts(run) {
  const family = familyOf(run);
  const spec = family?.spec || {};
  if (spec.fill === 'continuous') return { continuous: true };
  if (spec.fill !== 'widths') return {};
  const opts = { allowedWidthsIn: modulesSoldFor(family, run) };
  // A family that builds only its catalog's sizes packs a tall span from its
  // pantry widths and stands it at a pantry height (design 2026-10-05, D3).
  const talls = unitsOf(family, 'tall-pantry');
  if (talls.length) {
    opts.tallWidthsIn = [...new Set(talls.map((u) => u.w))];
    opts.tallHeightsIn = [...new Set(talls.map((u) => u.h))].sort(
      (a, b) => a - b
    );
  }
  return opts;
}

/**
 * The narrowest module a run's family sells, in inches: the family's smallest
 * offered width, else the base grid's `MIN_MODULE_IN`. A tall span narrower
 * than this packs as zero modules and one filler (`solveEqualModuleSequence`),
 * so it is the floor the write boundary and the panel hold a tall width to
 * (RP24). A continuous family has no width list and floors at `MIN_MODULE_IN`
 * — even a custom shop has a narrowest box worth building.
 */
function narrowestModuleIn(run) {
  const widths = familyWidthsIn(run);
  return widths.length ? Math.min(...widths) : MIN_MODULE_IN;
}

/**
 * Does this family sell a DISCRETE set of boxes, so that a width outside it
 * cannot be bought?
 *
 * True for every family EXCEPT the continuous one (user ruling 2026-09-08):
 * Stock and Semi-Custom both sell what their list says and nothing else, and
 * Custom is the family you go to when you want a width nobody stocks. A
 * family's list therefore governs a width someone PINS, not only the way its
 * fill divides a span.
 *
 * This deliberately supersedes the typed-width round trip of 2026-09-08, which
 * let a typed 15.8" stand on what is now a Semi-Custom run. The complaint
 * there was a number changing behind your back — the panel names the snap
 * under the field BEFORE the tick, so the substitution is no longer silent.
 */
function familySellsDiscreteWidths(run) {
  return familyOf(run)?.spec?.fill !== 'continuous';
}

/**
 * The width a fixed-bay override becomes under a run's family: the nearest
 * width the family sells, or the value itself under Custom, which sells every
 * width. Ties round UP — a bay the user widened should not come back
 * narrower.
 */
function snapWidthToFamily(widthIn, run) {
  if (!familySellsDiscreteWidths(run) || !Number.isFinite(widthIn))
    return widthIn;
  return nearestWidthIn(familyWidthsIn(run), widthIn);
}

/** The width in `widths` nearest `widthIn`, ties UP; `widthIn` for none. */
function nearestWidthIn(widths, widthIn) {
  if (!widths.length) return widthIn;
  return widths.reduce((best, w) =>
    Math.abs(w - widthIn) < Math.abs(best - widthIn) - 1e-9 ||
    (Math.abs(Math.abs(w - widthIn) - Math.abs(best - widthIn)) <= 1e-9 &&
      w > best)
      ? w
      : best
  );
}

/**
 * The width a TALL unit — a pantry — becomes under a run's family: the
 * nearest PANTRY width a vendor family makes (`familyFillOpts`'s
 * `tallWidthsIn`, the list its fill packs a tall span from), else the nearest
 * module width exactly as `snapWidthToFamily` answers. Ties round UP. Used
 * where a measured tall unit is brought into a line (the kitchen generator's
 * option #1 under the company's cabinet line).
 */
function snapTallWidthToFamily(widthIn, run) {
  const tall = familyFillOpts(run).tallWidthsIn;
  if (!tall || !Number.isFinite(widthIn))
    return snapWidthToFamily(widthIn, run);
  return nearestWidthIn(tall, widthIn);
}

module.exports = {
  CABINET_FAMILIES,
  DEFAULT_FAMILY_ID,
  familyById,
  familyOf,
  familyBuildsAnyDepth,
  isUnknownFamily,
  familyWidthsIn,
  familyFillOpts,
  cornerBlindLength,
  upperCornerBlindLength,
  cornerBlindSet,
  upperCornerBlindSet,
  cornerLSet,
  narrowestModuleIn,
  familySellsDiscreteWidths,
  snapWidthToFamily,
  snapTallWidthToFamily,
};
